import { Button, Flex } from "@chakra-ui/react";
import { useTranslation, Trans } from "react-i18next";
import React from "react";
import { useQuery } from "react-query";
import { Redirect, useHistory } from "react-router-dom";
import { useLock, UserContext } from "../../context";
import { rulesApiClient } from "../../apiConfig";
import { _basePath } from "../../config/index";

import { appInsights } from "../../common/AppInsightsErrorBoundary/AppInsights";
import { PlanetFitnessPromo } from "../../common/PlanetFitnessPromo";
import TemplateRequirement from "../../common/TemplateRequirement";
import ErrorMsg from "../../common/ErrorMsg";
import Document from "../../common/Document";
import Loader from "../../common/Loader";
import Stepper from "../../common/Stepper";

import { useApiCustomer, useApiTenant } from "../../context/ApiAuthContext";
import { RuleError } from "../../utils/RuleErrors";

const ScheduleRules = ({ user, loggedIn }) => {
  const { tenantId } = useApiTenant();
  const { customerName } = useApiCustomer();
  const { t } = useTranslation();
  const { userData, setUserData } = React.useContext(UserContext);
  const { clearLock } = useLock();
  const history = useHistory();
  const [isInvalid, setIsInvalid] = React.useState([]);
  const [currStep, setCurrStep] = React.useState(0);
  const [signingError, setSigningError] = React.useState(null);

  const ruleparam = {
    resources: userData?.lock?.resources,
    startTime: userData?.lock?.startTime,
    distance: 0,
    stage: "Schedule",
  };

  const onClearLock = () => {
    debugger;
    clearLock();
    history.go(-2);
  };
  const onCancel = () => {
    history.push(_basePath);
    clearLock();
  };
  const rulesQuery = useQuery(
    ["schedule_rules", user?.id],
    () =>
      rulesApiClient.userApiRuleEvaluatorPost(
        tenantId,
        "1.0",
        "en-US,en",
        ruleparam
      ),
    {
      cacheTime: 0,
      onSuccess: (data) => {
        const invalidRules = data?.data?.data?.details?.filter(
          (rule) => !rule.isValidated
        );
        setUserData({
          ...userData,
          ...{
            rulesToken: data?.data?.data?.id,
            requirementTypeId: data?.data?.data?.requirementTypeId,
          },
        });
        setIsInvalid(invalidRules);
      },
    }
  );
  const onSuccess = () => {
    const isLast = currStep === isInvalid?.length - 1;
    if (isLast) {
      rulesQuery.refetch();
    } else {
      setCurrStep(currStep + 1);
    }
  };
  const onError = (err) => {
    appInsights.trackException({
      exception: new Error(err),
    });
    setSigningError(
      err?.detail ? t(`error.${err?.detail}`) : t("error.unknown_error")
    );
  };
  const onRetry = () => {
    rulesQuery.refetch();
  };

  function SwitchComponent(props) {
    switch (props.rule.ruleId) {
      case "Radianse.RulesEngine.Core.TemplateRequirements":
        return (
          <TemplateRequirement
            key={props.rule.ruleId}
            document={props.rule}
            onComplete={onSuccess}
            onError={onError}
            onCancel={onCancel}
          />
        );
      case "Radianse.RulesEngine.Core.RequireDocuments":
        return (
          <Document
            document={props.rule}
            onSuccess={onSuccess}
            onError={onError}
            onCancel={onCancel}
          />
        );
      case "Radianse.RulesEngine.Core.ServicesConfigRule":
        // TODO: conditional loading based on string matching isn't ideal, This should be much more dynamic and moved to a dynamically loaded federated component
        if (customerName === "Planet Fitness") {
          return (
            <PlanetFitnessPromo
              key={props.rule.ruleId}
              onCancel={onCancel}
              onBack={onClearLock}
            />
          );
        } else {
          return (
            <RuleError
              ruleId={props.rule.ruleId}
              key={props.rule.ruleId}
              onCancel={onCancel}
              onBack={onClearLock}
            />
          );
        }
      default:
        return (
          <RuleError
            ruleId={props.rule.ruleId}
            values={props.rule?.metaData?.RuleConfiguration?.data}
            onCancel={onCancel}
          />
        );
    }
  }

  if (!loggedIn) {
    return <Redirect to={_basePath} />;
  }
  if (rulesQuery.isRefetching || rulesQuery.isLoading) {
    return (
      <Flex w={"full"} h={"full"}>
        <Loader text={t("loader.validating", "Validating...")} />
      </Flex>
    );
  }
  if (rulesQuery.isError || signingError) {
    return (
      <Flex w={"full"} h={"full"} direction={"column"} alignItems={"center"}>
        <ErrorMsg
          message={
            rulesQuery.error?.detail ||
            signingError ||
            t(
              "error.failed_validation",
              "Failed to validate schedule. Please retry or see a staff member for details."
            )
          }
        >
          <Button
            variant={"kioskPrimary"}
            onClick={onRetry}
            data-testid={"error-retry"}
          >
            <Trans i18nKey={"actions.retry"}>Retry</Trans>
          </Button>
        </ErrorMsg>
      </Flex>
    );
  }

  if (isInvalid && isInvalid.length >= 1) {
    return (
      <Stepper step={currStep}>
        {isInvalid.map((rule) => {
          return <SwitchComponent key={rule.ruleId} rule={rule} />;
        })}
      </Stepper>
    );
  }

  return <Redirect to={`${_basePath}/appointment/confirm`} />;
};

export default ScheduleRules;
